import clsx from 'clsx';
import dayjs from 'dayjs';
import Image from 'next/image';
import { memo } from 'react';

import ToggleText from '~/components/ui/ToggleText';
import {
  DISCUSSION_STATUS_APPROVED,
  DiscussionStatus,
} from '~/lib/discussions/types';
import { CommentV2Type } from '~/static/discussions/enums';
import DiscussionOptions from './DiscussionOptions';

export interface ICreatorReplyProps {
  id: number;
  avatarUrl: string;
  creatorUsername: string;
  replyDate: string;
  replyText: string;
  status: DiscussionStatus;
  allowOptions: boolean;
  deleteReplyUrl: string;
  className?: string;
  onDelete?: () => void;
}

/**
 * Creator Reply Component
 *
 * @param avatarUrl - The avatar url of the creator
 * @param creatorUsername - The username of the creator
 * @param replyDate - The date the reply was posted
 * @param replyText - The text of the reply
 */

function CreatorReply({
  avatarUrl,
  creatorUsername,
  replyDate,
  replyText,
  status,
  allowOptions,
  deleteReplyUrl,
  className = '',
  onDelete,
}: Readonly<ICreatorReplyProps>): JSX.Element {
  return (
    <div
      className={clsx([
        'flex flex-col items-start gap-2 self-stretch rounded-lg  p-4',
        status === DISCUSSION_STATUS_APPROVED ? 'bg-sky-100' : 'bg-iceberg',
      ])}
      data-testid="discussion-creator-reply"
    >
      <div className="flex w-full items-center justify-between gap-4">
        <span
          className={clsx([
            'text-xs font-semibold',
            status === DISCUSSION_STATUS_APPROVED ? 'text-bolt' : 'text-dollar',
          ])}
          data-testid="creator-reply-label"
        >
          {status === DISCUSSION_STATUS_APPROVED ? 'Founder Team' : 'Sumo'}
        </span>
        {allowOptions && (
          <div className="shrink-0">
            <DiscussionOptions
              deleteUrl={deleteReplyUrl}
              discussionType={CommentV2Type.Reply}
              deletedCallback={onDelete}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Image
          className="h-[50px] w-[50px] shrink-0 rounded-full object-cover"
          src={avatarUrl}
          alt={creatorUsername}
          height={50}
          width={50}
          loader={({ src }) => `${src}?w=50&h=50`}
          data-testid="creator-avatar"
        />
        <div>
          <p className="-mb-1 font-bold" data-testid="creator-username">
            {creatorUsername}
          </p>
          <span
            className="text-sm text-grace"
            data-testid="creator-posted-date"
          >
            {dayjs(replyDate).format('MMM D, YYYY')}
          </span>
        </div>
      </div>
      <ToggleText className={className} text={replyText} />
    </div>
  );
}

export default memo(CreatorReply);
